import * as React from 'react';
import { usePopout, PopoutPlacement, useClickAway, useMeasure } from '@hooks';
import { WithChildrenCommonProps } from '@types';
import { twMerge } from 'tailwind-merge';

export interface IDropdownProps {
  /** Defines the width of the Dropdown element */
  dropdownWidth?: number | string;
  /** Indicates whether the dropdown is opened */
  isOpen: boolean;
  /** Function to be executed when clicking outside the dropdown */
  onClickAway: VoidFunction;
  /** Indicates the position of the popout */
  placement?: PopoutPlacement;
  /** Reference of the element from which the dropdown is going to be shown */
  reference: HTMLElement | null;
}

export const Dropdown = ({
  children,
  dropdownWidth,
  isOpen,
  onClickAway,
  placement = 'bottom',
  reference,
  className: classNameProp,
  ...rest
}: WithChildrenCommonProps<IDropdownProps>): JSX.Element | null => {
  const { setPopoutElement } = usePopout(reference, placement, {
    enabled: isOpen,
    preventOverflow: !!dropdownWidth,
  });
  const clickAwayParentRef = React.useRef<HTMLElement | null>(null);
  const [ref, { width }] = useMeasure<HTMLElement>();

  React.useEffect(() => {
    reference && ref(reference);
  }, [ref, reference]);

  useClickAway(clickAwayParentRef, () => {
    isOpen && onClickAway();
  });

  const setRef = (element: HTMLDivElement) => {
    setPopoutElement(element);
    clickAwayParentRef.current = element;
  };

  if (!isOpen) {
    return null;
  }

  const tailwindClassName = twMerge(
    'rounded-2xl bg-white border-[1.5px] border-solid border-primary-main overflow-hidden z-[1004]',
    classNameProp
  );

  return (
    <div
      role="menu"
      ref={setRef}
      className={tailwindClassName}
      style={{ width: `${dropdownWidth || width}px` }}
      {...rest}
    >
      {children}
    </div>
  );
};
